import { t } from "@hakili/i18n";
import { useCallback, useEffect, useState } from "react";

import type { PaperKind, PaperView, VehiclePaperDoors, VehiclePapers } from "../session/vehicle-papers.js";
import { BOUTON_TEXTE } from "@hakili/ui";
import { ReadFailure } from "./read-failure.js";

/**
 * `A-DLR`, Arc A: **the vehicle's own papers, on the car's page.**
 *
 * The carte grise and the visite technique belong to the VEHICLE (the founder's S4-6 design): the dealer supplies
 * them once and they serve every dossier on the car. The visite technique carries its validity date, entered here by
 * the dealer; an expired one reads as missing. A car declared not registered is not asked for a carte grise.
 */
export function VehiclePapersCard({
  vehicleId,
  doors,
  onChanged,
}: {
  readonly vehicleId: string;
  readonly doors: VehiclePaperDoors;
  /** `A-DLR`, Arc B: told after a paper is recorded , the files waiting for it are filled, so « Pièces demandées » reloads. */
  readonly onChanged?: (() => void) | undefined;
}): React.JSX.Element {
  const [papers, setPapers] = useState<VehiclePapers | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  /** DEALER-READ-FAILURE-LISTS: the door's message when the READ failed (a write that failed stays in `message`). */
  const [readFailure, setReadFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<PaperKind | null>(null);
  const [validUntil, setValidUntil] = useState("");
  const [chassis, setChassis] = useState("");

  const load = useCallback(async (): Promise<void> => {
    const answer = await doors.readPapers(vehicleId);
    if (!answer.ok) {
      setReadFailure(answer.message);
      return;
    }
    setReadFailure(null);
    setPapers(answer.value);
    setChassis(answer.value.chassisNumber ?? "");
  }, [doors, vehicleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const choose = async (kind: PaperKind, file: File): Promise<void> => {
    setMessage(null);
    if (kind === "visiteTechnique" && validUntil === "") {
      setMessage(t("dealer.vehicules.papiersDateRequise"));
      return;
    }
    setBusy(kind);
    const sent = await doors.attachPaper({
      vehicleId,
      kind,
      file,
      ...(kind === "visiteTechnique" ? { validUntil } : {}),
    });
    setBusy(null);
    if (!sent.ok) {
      setMessage(sent.message);
      return;
    }
    await load();
    onChanged?.();
  };

  const saveChassis = async (): Promise<void> => {
    setMessage(null);
    const saved = await doors.declareChassis(vehicleId, chassis);
    if (!saved.ok) {
      setMessage(saved.message);
      return;
    }
    await load();
  };

  const required = papers?.required ?? [];
  const rows = (papers?.documents ?? []).filter((paper) => required.includes(paper.kind));

  // DEALER-READ-FAILURE-LISTS: a read that FAILED is said, in the recorded words; no paper rows (« Manquante » on all) and no form on nothing.
  if (readFailure !== null) {
    return (
      <div className="mb-4">
        <ReadFailure
          what="dealer.lecture.papiers"
          message={readFailure}
          onRetry={() => {
            void load();
          }}
          testId="vehicule-echec-papiers"
          retryTestId="vehicule-echec-papiers-reessayer"
        />
      </div>
    );
  }

  return (
    <section className="mb-4 rounded-lg border border-gray-200 p-3" data-testid="papiers-carte">
      <h3 className="text-sm font-semibold text-gray-900">{t("dealer.vehicules.papiersTitre")}</h3>
      <p className="mt-1 text-[11px] leading-relaxed text-gray-500">{t("dealer.vehicules.papiersAide")}</p>
      {papers?.registrationDeclared === false ? (
        <p className="mt-1 text-[11px] text-gray-500" data-testid="papiers-non-immatricule">
          {t("dealer.vehicules.papiersNonImmatricule")}
        </p>
      ) : null}
      <ul className="mt-2 space-y-2">
        {rows.map((paper) => (
          <PaperRow
            key={paper.kind}
            paper={paper}
            busy={busy === paper.kind}
            validUntil={validUntil}
            onValidUntil={setValidUntil}
            onChoose={(file) => {
              void choose(paper.kind, file);
            }}
          />
        ))}
      </ul>
      <div className="mt-3 flex items-end gap-2">
        <label className="flex-1 text-[11px] text-gray-600">
          {t("dealer.vehicules.chassisLabel")}
          <input
            className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            data-testid="papiers-chassis"
            value={chassis}
            onChange={(event) => {
              setChassis(event.target.value);
            }}
          />
        </label>
        <button
          type="button"
          className="rounded border border-gray-300 px-3 py-1 text-sm"
          data-testid="papiers-chassis-enregistrer"
          onClick={() => {
            void saveChassis();
          }}
        >
          {t("dealer.vehicules.chassisEnregistrer")}
        </button>
      </div>
      {message === null ? null : (
        <p className="mt-2 text-xs text-amber-700" role="alert" data-testid="papiers-message">
          {message}
        </p>
      )}
    </section>
  );
}

function PaperRow({
  paper,
  busy,
  validUntil,
  onValidUntil,
  onChoose,
}: {
  readonly paper: PaperView;
  readonly busy: boolean;
  readonly validUntil: string;
  readonly onValidUntil: (value: string) => void;
  readonly onChoose: (file: File) => void;
}): React.JSX.Element {
  const label =
    paper.kind === "carteGrise" ? t("dealer.vehicules.papierCarteGrise") : t("dealer.vehicules.papierVisite");
  const state =
    paper.standing === "absent"
      ? t("dealer.vehicules.papierManquant")
      : paper.standing === "expired"
        ? `${t("dealer.vehicules.papierExpire")} ${paper.validUntil ?? ""}`
        : paper.validUntil === null
          ? t("dealer.vehicules.papierEnRegle")
          : `${t("dealer.vehicules.papierValableJusquau")} ${paper.validUntil}`;
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm" data-testid={`papier-${paper.kind}`}>
      <span className="font-medium text-gray-900">{label}</span>
      <span
        className={paper.standing === "standing" ? "text-gray-700" : "text-amber-700"}
        data-testid={`papier-${paper.kind}-etat`}
      >
        {state}
      </span>
      {paper.url === null ? null : (
        <a className={`text-xs ${BOUTON_TEXTE}`} href={paper.url} target="_blank" rel="noreferrer">
          {t("dealer.vehicules.papierVoir")}
        </a>
      )}
      {paper.kind === "visiteTechnique" ? (
        <input
          type="date"
          className="rounded border border-gray-300 px-2 py-0.5 text-xs"
          aria-label={t("dealer.vehicules.papierDateFin")}
          data-testid="papier-visiteTechnique-date"
          value={validUntil}
          onChange={(event) => {
            onValidUntil(event.target.value);
          }}
        />
      ) : null}
      <label className="cursor-pointer rounded border border-gray-300 px-2 py-0.5 text-xs">
        {busy
          ? t("dealer.vehicules.papierEnvoi")
          : paper.standing === "absent"
            ? t("dealer.vehicules.papierAjouter")
            : t("dealer.vehicules.papierRemplacer")}
        <input
          type="file"
          className="sr-only"
          accept="image/jpeg,image/png,image/heic,application/pdf"
          data-testid={`papier-${paper.kind}-fichier`}
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) onChoose(file);
            event.target.value = "";
          }}
        />
      </label>
    </li>
  );
}
